import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  computed,
  signal,
} from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { ToastService } from '../../ui';

/**
 * Placeholder universel **TikTok Coming Soon** — sophistiqué et réutilisable.
 *
 * Modes d'affichage :
 *   - ``mode="page"``    : occupe tout l'écran (route /marketing/tiktok)
 *   - ``mode="section"`` : insertion dans une page existante (carte hero)
 *   - ``mode="card"``    : carte compacte (sidebar, dashboard)
 *
 * UI :
 *   - palette TikTok (cyan #25F4EE, magenta #FE2C55) sur fond noir glassmorphism
 *   - animations purement CSS : ondes audio, particules float, glitch text,
 *     glow pulse, équaliseur — toutes en `prefers-reduced-motion: respect`
 *   - logo TikTok inline en SVG (note de musique stylisée double)
 *   - bouton "Notify me" qui pousse un toast (les vraies notifications
 *     seront branchées sur l'event `tiktok_waitlist_subscribed` quand
 *     l'intégration sera prête)
 *
 * i18n : toutes les chaînes via Transloco (clés `marketing.tiktok.*`).
 * Aucune string hardcodée.
 */
@Component({
  selector: 'app-tiktok-coming-soon',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  templateUrl: './tiktok-coming-soon.component.html',
  styleUrls: ['./tiktok-coming-soon.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TiktokComingSoonComponent {
  /** Mode d'affichage. */
  @Input() mode: 'page' | 'section' | 'card' = 'page';
  /** Cacher le bouton de pré-inscription (mode card par défaut). */
  @Input() showCta = true;
  /** Cacher la liste des features prévues. */
  @Input() showFeatures = true;
  /** Override de la clé i18n du titre principal. */
  @Input() titleKeyOverride?: string;

  readonly subscribed = signal<boolean>(this.readPersisted());

  readonly bars = Array.from({ length: 18 }, (_, i) => i);
  readonly particles = Array.from({ length: 12 }, (_, i) => i);

  readonly featuresPlanned = [
    { icon: 'movie_filter',  key: 'marketing.tiktok.feature_publish' },
    { icon: 'auto_awesome',  key: 'marketing.tiktok.feature_ai' },
    { icon: 'forum',         key: 'marketing.tiktok.feature_inbox' },
    { icon: 'campaign',      key: 'marketing.tiktok.feature_ads' },
    { icon: 'shopping_bag',  key: 'marketing.tiktok.feature_shop' },
    { icon: 'analytics',     key: 'marketing.tiktok.feature_insights' },
  ];

  readonly titleKey = computed(
    () => this.titleKeyOverride ?? 'marketing.tiktok.title',
  );

  constructor(
    private readonly toast: ToastService,
    private readonly t: TranslocoService,
  ) {}

  notifyMe(): void {
    if (this.subscribed()) return;
    this.subscribed.set(true);
    try {
      localStorage.setItem('vd_tiktok_waitlist', '1');
    } catch {
      // private mode safari : on tolère
    }
    this.toast.success(
      this.t.translate('marketing.tiktok.toast.subscribed_desc'),
      this.t.translate('marketing.tiktok.toast.subscribed_title'),
    );
  }

  private readPersisted(): boolean {
    try {
      return localStorage.getItem('vd_tiktok_waitlist') === '1';
    } catch {
      return false;
    }
  }
}
